import { useNavigate } from "#src/Routers/hooks";
import {
  InvestigationHistoryFilterArea,
  type InvestigationHistoryFiltersType,
} from "#src/batteries-included-components/FilterAreas/InvestigationHistoryFilterAreas";
import { buildInvestigationHistorySearchFilters } from "#src/routes/investigations/history/buildInvestigationHistorySearchFilters";
import {
  useInvestigationsInfiniteList,
  useUpdateInvestigation,
} from "#src/components/hooks/adapters/useInvestigations";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { InvestigationHistoryHeader } from "#src/routes/investigations/history/InvestigationHistoryHeader";
import { InvestigationHistoryList } from "#src/routes/investigations/history/InvestigationHistoryList";
import { RenameInvestigationTitleDialog } from "#src/routes/investigations/history/RenameInvestigationTitleDialog";
import {
  INVESTIGATION_HISTORY_PANEL_COLLAPSED_WIDTH_PX,
  INVESTIGATION_HISTORY_DRAWER_KEY,
  INVESTIGATION_HISTORY_PANEL_EXPANDED_WIDTH_PX,
  INVESTIGATION_HISTORY_PANEL_SCROLL_AREA_TEST_ID,
} from "#src/routes/investigations/history/consts";
import { useInvestigationHistoryPanelScrollbarOverlay } from "#src/routes/investigations/history/useInvestigationHistoryPanelScrollbarOverlay";
import { getInvestigationHistoryTimelinePanelStore } from "#src/routes/investigations/history/investigationHistoryTimelinePanelStore";
import { InvestigationsRoutePath } from "#src/routes/investigations";
import {
  Button,
  Icon,
  TimelinePanel,
  TimelinePanelMode,
  TimelinePanelStoreProvider,
  useTimelinePanelStore,
} from "@validereinc/common-components";
import type { InvestigationType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction,
} from "react";
import styles from "./InvestigationHistoryPanel.module.scss";

const cx = classNames.bind(styles);

type InvestigationHistoryPanelProps = {
  selectedInvestigationId?: string;
};

type InvestigationHistoryPanelDrawerProps = {
  selectedInvestigationId?: string;
  setIsPanelCollapsed: (value: SetStateAction<boolean>) => void;
};

const InvestigationHistoryPanelDrawer = ({
  selectedInvestigationId,
  setIsPanelCollapsed,
}: InvestigationHistoryPanelDrawerProps) => {
  const navigate = useNavigate();
  const { isOpen } = useTimelinePanelStore();
  const [filterConfigStorageKey] = useStorageKey(
    ["filter-configuration"],
    "investigation-history-filters"
  );
  const [storedFilters] = useStickyState<InvestigationHistoryFiltersType>(
    {},
    filterConfigStorageKey
  );
  const searchFilters = useMemo(
    () =>
      buildInvestigationHistorySearchFilters({
        title: storedFilters.title,
        status: storedFilters.status,
      }),
    [storedFilters.status, storedFilters.title]
  );
  const investigationsQuery = useInvestigationsInfiniteList({
    filters: searchFilters,
  });
  const { data: investigationsData, isLoading: isLoadingInvestigations } =
    investigationsQuery;
  const isInitialPanelLoading = isLoadingInvestigations && !investigationsData;
  const [scrollAreaElement, setScrollAreaElement] =
    useState<HTMLDivElement | null>(null);
  const scrollbarMetrics = useInvestigationHistoryPanelScrollbarOverlay(
    scrollAreaElement,
    investigationsData
  );
  const [investigationToRename, setInvestigationToRename] =
    useState<InvestigationType | null>(null);
  const { mutate: updateInvestigationArchivedState } = useUpdateInvestigation({
    successMessage: (_data, variables) =>
      variables?.data?.archived
        ? "Successfully archived investigation"
        : "Successfully unarchived investigation",
  });

  useEffect(() => {
    setIsPanelCollapsed(!isOpen);
  }, [isOpen, setIsPanelCollapsed]);

  const handleNavigateToNewInvestigation = useCallback(() => {
    navigate({ pathname: InvestigationsRoutePath.toLink() });
  }, [navigate]);

  const handleRenameRequest = useCallback(
    (investigation: InvestigationType) => {
      window.requestAnimationFrame(() => {
        setInvestigationToRename(investigation);
      });
    },
    []
  );

  const handleCloseRenameDialog = useCallback(() => {
    setInvestigationToRename(null);
  }, []);

  const handleSetInvestigationArchived = useCallback(
    (investigation: InvestigationType, archived: boolean) => {
      updateInvestigationArchivedState({
        id: investigation.id,
        data: { archived },
      });
    },
    [updateInvestigationArchivedState]
  );

  return (
    <TimelinePanel.Drawer
      className={cx("timeline-panel", {
        "timeline-panel--collapsed": !isOpen,
      })}
      drawerKey={INVESTIGATION_HISTORY_DRAWER_KEY}
      expandedWidth={INVESTIGATION_HISTORY_PANEL_EXPANDED_WIDTH_PX}
      shouldRenderPortal={false}
      size="sm"
      mode={TimelinePanelMode.RAIL}
      minimizedWidth={INVESTIGATION_HISTORY_PANEL_COLLAPSED_WIDTH_PX}
    >
      <InvestigationHistoryHeader />
      <TimelinePanel.Content
        isLoading={isInitialPanelLoading}
        className={cx("timeline-panel__content")}
        style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {isOpen ? (
          <div className={cx("timeline-panel__content-actions")}>
            <Button
              className={cx("new-investigation-button")}
              variant="outline"
              size="small"
              onClick={handleNavigateToNewInvestigation}
              slotLeft={
                <Icon
                  variant="plus-circle"
                  size={20}
                />
              }
            >
              New Investigation
            </Button>
            <InvestigationHistoryFilterArea
              filterConfigStorageKey={filterConfigStorageKey}
            />
          </div>
        ) : null}
        <div className={cx("timeline-panel__list-scroll-shell")}>
          <div
            ref={setScrollAreaElement}
            className={cx("timeline-panel__list-scroll")}
            data-investigation-history-scroll-area
            data-testid={INVESTIGATION_HISTORY_PANEL_SCROLL_AREA_TEST_ID}
          >
            <InvestigationHistoryList
              selectedInvestigationId={selectedInvestigationId}
              onRenameRequest={handleRenameRequest}
              onSetArchived={handleSetInvestigationArchived}
              filters={storedFilters}
              investigationsQuery={investigationsQuery}
            />
          </div>
          {scrollbarMetrics.isScrollbarVisible ? (
            <div
              className={cx("timeline-panel__scrollbar-track")}
              aria-hidden="true"
            >
              <div
                className={cx("timeline-panel__scrollbar-thumb")}
                style={{
                  height: `${scrollbarMetrics.thumbHeightPx}px`,
                  transform: `translateY(${scrollbarMetrics.thumbOffsetPx}px)`,
                }}
              />
            </div>
          ) : null}
        </div>
        {investigationToRename ? (
          <RenameInvestigationTitleDialog
            investigation={investigationToRename}
            isOpen
            onClose={handleCloseRenameDialog}
          />
        ) : null}
      </TimelinePanel.Content>
    </TimelinePanel.Drawer>
  );
};

export const InvestigationHistoryPanel = ({
  selectedInvestigationId,
}: InvestigationHistoryPanelProps) => {
  const [panelCollapsedStorageKey] = useStorageKey(
    ["app-configuration"],
    "is-investigation-history-panel-collapsed"
  );
  const [isPanelCollapsed, setIsPanelCollapsed] = useStickyState(
    false,
    panelCollapsedStorageKey
  );
  const isPanelExpanded = !isPanelCollapsed;

  return (
    <TimelinePanelStoreProvider
      store={getInvestigationHistoryTimelinePanelStore(isPanelExpanded)}
    >
      <InvestigationHistoryPanelDrawer
        selectedInvestigationId={selectedInvestigationId}
        setIsPanelCollapsed={setIsPanelCollapsed}
      />
    </TimelinePanelStoreProvider>
  );
};
